import {
  type Dispatch,
  type MutableRefObject,
  type PointerEvent as ReactPointerEvent,
  type SetStateAction,
  useCallback,
} from 'react'
import { toast } from 'sonner'
import { LOCKED_LINK_TARGET_MESSAGE } from '@/lib/text-linking/chain-content-edit-policy'
import { PORT_HIT_SIZE } from '@/lib/text-linking/text-linking-geometry'
import { clampTextBoxDimensions, meetsTextDragThreshold } from '@/lib/text/text-tool-create'
import type { CenterCanvasConfig } from '../types/center-canvas-config'
import type { TextDragCreateState } from '../types/canvas-drag-state'
import type { TextLinkingSession } from '../types/text-linking-session'
import type { useTextLinking } from './use-text-linking'

type TextLinking = ReturnType<typeof useTextLinking>

/**
 * Pasteboard pointer gestures of text linking (WMPCM-6852): picking a link target or starting a
 * drag-to-size a new linked frame while linking is armed, tracking the linking cursor, and the
 * Text Tool's hover "+" over a linkable frame.
 */
export function useTextLinkDrag({
  textLinking,
  setTextLinking,
  linkedTextDragStartRef,
  findLinkableFrameAtDocPoint,
  announceTextLink,
  setTextToolHoverFrameId,
  isInTextCreationMode,
  textDragCreateRef,
  scaleRef,
  getCanvasPointFromPointerEvent,
  onTextFramesLinked,
  onCreateLinkedTextFrame,
}: {
  textLinking: TextLinkingSession | null
  setTextLinking: Dispatch<SetStateAction<TextLinkingSession | null>>
  linkedTextDragStartRef: TextLinking['linkedTextDragStartRef']
  findLinkableFrameAtDocPoint: TextLinking['findLinkableFrameAtDocPoint']
  announceTextLink: (message: string) => void
  setTextToolHoverFrameId: Dispatch<SetStateAction<string | null>>
  isInTextCreationMode: boolean
  textDragCreateRef: MutableRefObject<TextDragCreateState | null>
  scaleRef: MutableRefObject<number>
  getCanvasPointFromPointerEvent: (
    clientX: number,
    clientY: number
  ) => { canvasX: number; canvasY: number } | null
  onTextFramesLinked: CenterCanvasConfig['onTextFramesLinked']
  onCreateLinkedTextFrame: CenterCanvasConfig['onCreateLinkedTextFrame']
}) {
  const handleTextLinkPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>): boolean => {
      // Text linking: pointerdown on a port/frame hit target is stopped from bubbling here by
      // TextLinkingLayer itself, so reaching this handler while linking means empty-canvas —
      // start a drag-to-size gesture for the new linked frame (AC12); finalized on pointer-up.
      if (!textLinking || e.button !== 0) return false

      e.preventDefault()
      const pt = getCanvasPointFromPointerEvent(e.clientX, e.clientY)

      if (!pt) {
        setTextLinking(null)
        return true
      }

      // Reaching this handler does NOT prove the click was on empty canvas. A shape is
      // pointer-transparent while linking is armed — `isInShapeCreationMode` folds in
      // `isTextLinkingActive`, which ShapeRenderer turns into `pointerEvents: 'none'` — so a
      // click on a target *shape* falls through to here. Without this hit-test it read as empty
      // canvas and drag-created a brand-new text box on top of the shape, then linked that
      // instead; the shape itself never joined the chain (WMPCM-9070). Text frames never had the
      // bug because the TextManager layer keeps its pointer events during linking.
      const target = findLinkableFrameAtDocPoint(pt.canvasX, pt.canvasY, {
        excludeId: textLinking.sourceId,
        includeLocked: true,
      })

      // A locked frame is not a link target (`canLink` → 'target-locked'), and it must not fall
      // through to the empty-canvas branch below either: that drag-created a new linked frame on
      // top of it, which looked like the locked frame had accepted the link (WMPCM-10578 #3).
      // Refuse the click and say why. The session stays armed so another target can be picked;
      // Escape still cancels it.
      if (target?.locked) {
        toast.error(LOCKED_LINK_TARGET_MESSAGE)
        announceTextLink(LOCKED_LINK_TARGET_MESSAGE)
        return true
      }

      if (target) {
        onTextFramesLinked?.(textLinking.sourceId, target.id)
        announceTextLink('Text frames linked.')
        setTextLinking(null)
        return true
      }

      e.currentTarget.setPointerCapture(e.pointerId)
      linkedTextDragStartRef.current = {
        pointerId: e.pointerId,
        sourceId: textLinking.sourceId,
        startCanvasX: pt.canvasX,
        startCanvasY: pt.canvasY,
      }

      return true
    },
    [
      announceTextLink,
      getCanvasPointFromPointerEvent,
      textLinking,
      findLinkableFrameAtDocPoint,
      onTextFramesLinked,
      linkedTextDragStartRef,
      setTextLinking,
    ]
  )

  const handleTextLinkPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (textLinking) {
        const pt = getCanvasPointFromPointerEvent(e.clientX, e.clientY)

        if (pt) {
          setTextLinking((prev) =>
            prev ? { ...prev, cursorDocPoint: { x: pt.canvasX, y: pt.canvasY } } : prev
          )
        }
      }
    },
    [getCanvasPointFromPointerEvent, textLinking, setTextLinking]
  )

  const handleTextToolHoverPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      // Text Tool hover "+" (WMPCM-6852 AC12) — while the Text Tool is active, not already
      // linking, and not mid a plain-text-box drag-create, track which linkable frame (if any)
      // the pointer is over so TextLinkingLayer can show a single "+" for it.
      if (isInTextCreationMode && !textLinking && !textDragCreateRef.current) {
        const pt = getCanvasPointFromPointerEvent(e.clientX, e.clientY)
        // This hit-test runs in document space, but the "+" it's guarding is drawn by
        // TextLinkingLayer in screen pixels now (a sibling of the canvas's own scale wrapper,
        // same as selection chrome) — so its half-size margin has to come back down to doc
        // space by the current zoom, or the margin would only be correct at 100%.
        const hit = pt
          ? findLinkableFrameAtDocPoint(pt.canvasX, pt.canvasY, {
              // Read through `scaleRef` rather than `scale` so this pointermove callback is not
              // rebuilt on every zoom step; the ref is reassigned on each render.
              bottomMargin: PORT_HIT_SIZE / 2 / Math.max(scaleRef.current, 0.0001),
            })
          : null

        setTextToolHoverFrameId((prev) => (prev === (hit?.id ?? null) ? prev : (hit?.id ?? null)))
      }
    },
    [
      getCanvasPointFromPointerEvent,
      textLinking,
      isInTextCreationMode,
      findLinkableFrameAtDocPoint,
      setTextToolHoverFrameId,
      textDragCreateRef,
      scaleRef,
    ]
  )

  const handleTextLinkPointerUp = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>): boolean => {
      // Finalize the linked-frame drag-create gesture (WMPCM-6852 AC12) started in
      // `handleTextLinkPointerDown`. A plain click (no drag past the threshold) falls back to
      // today's clone-source-size behavior by omitting `size`.
      const linkedDrag = linkedTextDragStartRef.current

      if (!linkedDrag || e.pointerId !== linkedDrag.pointerId) return false

      e.preventDefault()
      linkedTextDragStartRef.current = null

      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId)
      }

      const endPtRaw = getCanvasPointFromPointerEvent(e.clientX, e.clientY)
      const endPoint =
        textLinking?.cursorDocPoint ??
        (endPtRaw
          ? { x: endPtRaw.canvasX, y: endPtRaw.canvasY }
          : { x: linkedDrag.startCanvasX, y: linkedDrag.startCanvasY })
      const dx = endPoint.x - linkedDrag.startCanvasX
      const dy = endPoint.y - linkedDrag.startCanvasY
      const canvasScale = scaleRef.current
      const isDrag = meetsTextDragThreshold(dx, dy, canvasScale)
      const boxX = Math.min(linkedDrag.startCanvasX, endPoint.x)
      const boxY = Math.min(linkedDrag.startCanvasY, endPoint.y)

      onCreateLinkedTextFrame?.({
        sourceId: linkedDrag.sourceId,
        docPoint: { x: boxX, y: boxY },
        ...(isDrag
          ? { size: clampTextBoxDimensions(Math.abs(dx), Math.abs(dy), canvasScale) }
          : {}),
      })

      announceTextLink('Linked text frame created.')
      setTextLinking(null)
      return true
    },
    [
      announceTextLink,
      getCanvasPointFromPointerEvent,
      textLinking,
      onCreateLinkedTextFrame,
      linkedTextDragStartRef,
      setTextLinking,
      scaleRef,
    ]
  )

  return {
    handleTextLinkPointerDown,
    handleTextLinkPointerMove,
    handleTextToolHoverPointerMove,
    handleTextLinkPointerUp,
  }
}
